import { useCallback, useEffect, useMemo, useState } from 'react';
/**
 * A hook for selection on lists of items of type DTO. Useful when creating tables - to easily have that "select all"
 * checkbox. In needs list of objects and a predicate to get an id from those object. The result of this hook work is
 * a list containing a set of these ids and several functions to control/toggle the items in that list.
 *
 * Another use might be "toggling" items (like when you have a table with expandable rows)
 * @param items - List of items (ones you are selecting, list of DTOs)
 * @param getId - A predicate function, that returns a string (id) by which the items (DTOs) are identified.
 * @param removeMissingItemsFromSelection - If set to true, when the items set changes - the selectedIds will always
 * contain only ids of available items (false by default, so changing items won't affect the selection)
 * @returns a SelectionState object that has the state and methods for using and changing the selection
 */
export const useSelectionState = (items, getId, removeMissingItemsFromSelection = false, isSelectedAllByDefault = false) => {
    const allItemIds = useMemo(() => items.map(getId), [items, getId]);
    const [selectedIds, setSelectedIds] = useState(isSelectedAllByDefault ? allItemIds : []);
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const toggleId = useCallback((toggledItemId) => setSelectedIds((currentSelectedItems) => {
        if (currentSelectedItems.includes(toggledItemId)) {
            return currentSelectedItems.filter((itemId) => toggledItemId !== itemId);
        }
        return [...currentSelectedItems, toggledItemId];
    }), []);
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const toggleItem = useCallback((item) => toggleId(getId(item)), [toggleId, getId]);
    const isSelectedAll = useMemo(() => (items.length ? items.every((item) => selectedIds.includes(getId(item))) : false), [items, getId, selectedIds]);
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const selectAll = useCallback(() => {
        setSelectedIds(allItemIds);
    }, [setSelectedIds, allItemIds]);
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const deselectAll = useCallback(() => {
        setSelectedIds([]);
    }, [setSelectedIds]);
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const isSelected = useCallback((item) => selectedIds.includes(getId(item)), [selectedIds, getId]);
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const getToggle = useCallback((item) => () => toggleItem(item), [toggleItem]);
    const filteredSelectedIds = useMemo(() => (removeMissingItemsFromSelection ? selectedIds.filter((id) => allItemIds.includes(id)) : selectedIds), [allItemIds, selectedIds, removeMissingItemsFromSelection]);
    const isSomethingSelected = filteredSelectedIds.length > 0;
    const indeterminate = isSomethingSelected && !isSelectedAll;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const toggleSelectAll = useCallback(() => {
        if (isSomethingSelected) {
            deselectAll();
        }
        else {
            selectAll();
        }
    }, [isSomethingSelected, deselectAll, selectAll]);
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const addIds = useCallback((addedIDs) => setSelectedIds((currentIds) => {
        const missingIds = addedIDs.filter((id) => !currentIds.includes(id));
        return missingIds.length > 0 ? [...currentIds, ...missingIds] : currentIds;
    }), []);
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const removeIds = useCallback((removedIDs) => setSelectedIds((currentIds) => {
        let somethingRemoved = false;
        const newIDs = currentIds.filter((id) => {
            if (removedIDs.includes(id)) {
                somethingRemoved = true;
                return false;
            }
            return true;
        });
        return somethingRemoved ? newIDs : currentIds;
    }), []);
    useEffect(() => {
        if (removeMissingItemsFromSelection) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSelectedIds((currentIds) => {
                let somethingRemoved = false;
                const newIds = currentIds.filter((id) => {
                    if (!allItemIds.includes(id)) {
                        somethingRemoved = true;
                        return false;
                    }
                    return true;
                });
                return somethingRemoved ? newIds : currentIds;
            });
        }
    }, [removeMissingItemsFromSelection, allItemIds]);
    return useMemo(() => ({
        isSelectedAll,
        toggleSelectAll,
        isSelected,
        getToggle,
        selectedIds: filteredSelectedIds,
        setSelectedIds,
        toggleId,
        toggleItem,
        isSomethingSelected,
        selectAll,
        deselectAll,
        addIds,
        removeIds,
        allItemIds,
        indeterminate,
    }), [
        isSelectedAll,
        toggleSelectAll,
        isSelected,
        getToggle,
        filteredSelectedIds,
        toggleId,
        toggleItem,
        isSomethingSelected,
        selectAll,
        deselectAll,
        addIds,
        removeIds,
        allItemIds,
        indeterminate,
    ]);
};
// A default predicate to return the id from any object that has an `id` field.
useSelectionState.selectId = ({ id }) => id;
// Same, but to be used with DTOs whose ID might be undefined
useSelectionState.selectIdUndef = ({ id }) => (id || '');
